import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  ActivatedRouteSnapshot,
  ActivatedRoute,
  Data,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  ResolveStart,
  Router,
  RouterModule,
} from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import app from './core/services/firebase';
import { CommonModule } from '@angular/common';
import { loadIcons } from './shared/utils/utils';
import { MatIconModule, MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { ResponsiveService } from './core/services/utils/responsive.service';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { CustomerLoadingService } from './core/services/customer-loading/customer-loading.service';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { WebHeaderComponent, WebFooterComponent } from './shared';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { CartService } from './core/services/cart/cart.service';
import { ShopSelectionStore } from './core/state/shop-selection.store';
import { ShopSelectionService } from './core/services/shop-selection/shop-selection.service';
import { HubspotService } from './core/services/hubspot/hubspot.service';
import { AdBannerTrackingService } from './core/services/ad-banners/ad-banner-tracking.service';
import { environment } from '../environments/environment';
import { Subscription } from 'rxjs';
import { filter, startWith } from 'rxjs/operators';
import { VersionCheckService } from './core/services/version-check/version-check.service';

declare global {
  interface Window {
    clarity?: (...args: any[]) => void;
    _hsq?: any[];
    HubSpotConversations?: any;
    gtag?: (...args: any[]) => void;
  }
}

const GA_MEASUREMENT_ID = 'G-3M9KLJLQ4D';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [
    RouterModule,
    MatButtonModule,
    MatInputModule,
    MatDialogModule,
    MatSnackBarModule,
    MatFormFieldModule,
    CommonModule,
    MatProgressSpinnerModule,
    WebHeaderComponent,
    WebFooterComponent,
    MatIconModule,
    ScrollingModule,
  ],
  providers: [CustomerLoadingService],
})
export class AppComponent implements OnInit, OnDestroy {
  app = app;
  showFooter = false;
  showSearchButton = false;
  isMobile: boolean = false;
  isLoading: boolean;
  showScrollToTopButton = false;
  isFirstLogin: boolean = false;
  preventShoping: boolean = false;

  showHeader = false;
  shopHeader = false;
  pageHeader = false;
  noShadow = false;
  isOpeningFirstPage = false;

  @ViewChild('content') content: ElementRef;
  private routerEventsSub?: Subscription;
  private customersSub?: Subscription;
  private routeDataSub?: Subscription;
  private shopSelectionSub?: Subscription;
  private currentCustomerId: number | null = null;
  private currentPath: string | null = null;

  constructor(
    private matIconRegistry: MatIconRegistry,
    private domSanitizer: DomSanitizer,
    private translate: TranslateService,
    private customerLoadingService: CustomerLoadingService,
    private responsiveService: ResponsiveService,
    private shopSelectionStore: ShopSelectionStore,
    private shopSelectionService: ShopSelectionService,
    private hubspotService: HubspotService,
    private cartService: CartService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private adBannerTrackingService: AdBannerTrackingService,
    private versionCheckService: VersionCheckService
  ) {
    translate.setFallbackLang('de');
    translate.use('de');
  }

  ngOnInit() {
    this.adBannerTrackingService.setSessionId();
    this.versionCheckService.init();

    if (environment.production) {
      this.addGoogleAnalyticsPixel();
      this.addClarityPixel();
      this.appendChatCode();
      this.routerEventsSub = this.router.events
        .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
        .subscribe(e => {
          const url = e.urlAfterRedirects;
          if (typeof window.clarity === 'function') {
            window.clarity('set', 'page', url);
            window.clarity('event', 'route_change');
          }
        });
    }

    loadIcons(this.matIconRegistry, this.domSanitizer);
    this.shopSelectionSub = this.shopSelectionService.connect();
    this.customersSub = this.shopSelectionStore.customer$.subscribe(
      customer => {
        try {
          const newId = customer ? customer.id : null;
          if (newId === null) {
            this.currentCustomerId = null;
            this.hubspotService.removeWidget();
            return;
          }
          if (!this.hubspotService.isLoaded()) {
            this.hubspotService.init();
            this.currentCustomerId = newId;
            return;
          }
          if (this.currentCustomerId !== newId) {
            this.hubspotService.resetAndReloadWidget();
            this.currentCustomerId = newId;
          }
        } catch (err) {
          console.warn('HubSpot customer change handling failed', err);
        }
      }
    );

    this.responsiveService.isMobile$.subscribe(isMobile => {
      this.isMobile = isMobile;
    });
    this.customerLoadingService.loading$.subscribe(isLoading => {
      this.isLoading = isLoading;
    });
    this.shopSelectionStore.isFirstLogin$.subscribe(isFirstLogin => {
      this.isFirstLogin = isFirstLogin;
    });
    this.cartService.getPreventShoping().subscribe(preventShoping => {
      this.preventShoping = preventShoping;
      if (preventShoping) {
        this.content.nativeElement.scrollTo({ top: 0 });
      }
    });

    this.routeDataSub = this.router.events
      .pipe(startWith(null))
      .subscribe(event => {
        // Until the first navigation ends the route data isn't known yet, and
        // defaulting to visible flashes the header on pages that hide it.
        if (event === null && !this.router.navigated) return;
        if (event instanceof ResolveStart && !this.router.navigated) {
          this.isOpeningFirstPage = true;
          this.applyLayout(deepestSnapshot(event.state.root).data);
        } else if (event === null || event instanceof NavigationEnd) {
          this.isOpeningFirstPage = false;
          if (event instanceof NavigationEnd)
            this.resetScrollOnPageChange(event.urlAfterRedirects);
          let currentRoute = this.activatedRoute.root;
          while (currentRoute.firstChild) {
            currentRoute = currentRoute.firstChild;
          }
          this.applyLayout(currentRoute.snapshot.data);
          if (!this.shopHeader) {
            this.cartService.setPreventShoping(false);
          }
        } else if (
          event instanceof NavigationCancel ||
          event instanceof NavigationError
        ) {
          this.isOpeningFirstPage = false;
        }
      });
  }

  private applyLayout(data: Data): void {
    this.showHeader = data['showHeader'] !== false;
    this.showFooter = data['showFooter'] !== false;
    this.shopHeader = data['shopHeader'] === true;
    this.pageHeader = data['pageHeader'] === true;
    this.noShadow = data['noShadow'] === true;
  }

  ngOnDestroy() {
    this.routerEventsSub?.unsubscribe();
    this.customersSub?.unsubscribe();
    this.routeDataSub?.unsubscribe();
    this.shopSelectionSub?.unsubscribe();
    this.versionCheckService.destroy();
  }

  private resetScrollOnPageChange(url: string): void {
    const path = url.split(/[?#]/)[0];
    if (this.currentPath !== null && this.currentPath !== path) {
      this.content?.nativeElement.scrollTo({ top: 0 });
    }
    this.currentPath = path;
  }

  onScroll(event: any): void {
    const scrollTop = event.target.scrollTop;
    this.showScrollToTopButton = scrollTop > 90;
  }

  scrollToTop() {
    if (this.content && this.content.nativeElement) {
      this.content.nativeElement.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  private addGoogleAnalyticsPixel() {
    try {
      const loader = document.createElement('script');
      loader.async = true;
      loader.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
      document.head.appendChild(loader);

      const config = document.createElement('script');
      config.type = 'text/javascript';
      config.innerHTML = `
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
        gtag('config', '${GA_MEASUREMENT_ID}');`;
      document.head.appendChild(config);
    } catch (err) {
      console.error('Error appending Google Analytics Pixel');
      console.error(err);
    }
  }

  private addClarityPixel() {
    try {
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.innerHTML = `
        (function(c,l,a,r,i,t,y){c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);})(window, document, "clarity", "script", "qjdilxffwf");`;
      document.head.appendChild(script);
    } catch (err) {
      console.error('Error appending Clarity Pixel');
      console.error(err);
    }
  }

  private appendChatCode() {
    try {
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.innerHTML = `
        (function(w,d,u){
          var s=d.createElement('script');s.async=true;s.src=u+'?'+(Date.now()/60000|0);
          var h=d.getElementsByTagName('script')[0];h.parentNode.insertBefore(s,h);
        })(window,document,'https://cdn.bitrix24.de/b1480931/crm/site_button/loader_8_q88cl9.js');`;
      document.head.appendChild(script);
    } catch (err) {
      console.error('Error appending Chat Code');
      console.error(err);
    }
  }
}

function deepestSnapshot(
  route: ActivatedRouteSnapshot
): ActivatedRouteSnapshot {
  while (route.firstChild) route = route.firstChild;
  return route;
}
